import { useState } from "react";
import {
  colors,
  effects,
  layout,
  typography,
} from "../../design/tokens";
import { useConsent } from "../../providers/ConsentProvider";
import { useIsCompactView } from "../../core_stores/useIsCompactViewStore";

/**
 * GDPR Cookie Consent Banner
 *
 * Displays as a compact card in the bottom-right corner.
 * Uses Darvin's premium dark mode design system.
 */
export function CookieConsentBanner() {
  const {
    showBanner,
    showPreferencesModal,
    acceptAll,
    rejectAll,
    openPreferences,
    closePreferences,
    savePreferences,
    consent,
  } = useConsent();
  const isCompactView = useIsCompactView();

  // Local state for preferences modal toggles
  const [analyticsEnabled, setAnalyticsEnabled] = useState(
    consent?.analytics ?? false
  );
  const [marketingEnabled, setMarketingEnabled] = useState(
    consent?.marketing ?? false
  );

  // Reset toggle states when modal opens
  const handleOpenPreferences = () => {
    setAnalyticsEnabled(consent?.analytics ?? false);
    setMarketingEnabled(consent?.marketing ?? false);
    openPreferences();
  };

  const handleSavePreferences = () => {
    savePreferences(analyticsEnabled, marketingEnabled);
  };

  if (!showBanner && !showPreferencesModal) {
    return null;
  }

  return (
    <>
      {/* Cookie Banner - Compact card in bottom-right */}
      {showBanner && !showPreferencesModal && (
        <div
          className={`
            fixed ${isCompactView ? "bottom-4 left-4 right-4" : "bottom-6 right-6"}
            ${isCompactView ? "w-auto" : "w-[340px]"}
            bg-dark-surface-secondary
            border ${colors.border.primary}
            ${effects.borderRadius.xl}
            ${layout.zIndex.modal}
            shadow-[0_8px_32px_0_rgba(0,0,0,0.6)]
            animate-slide-up
            p-5
          `}
        >
          {/* Header */}
          <h3
            className={`${typography.size.base} ${typography.weight.semibold} ${colors.text.primary} mb-2`}
          >
            Choose your cookies
          </h3>

          {/* Description */}
          <p className={`${typography.size.sm} ${colors.text.secondary} mb-4`}>
            We use cookies to enhance your experience and keep your data secure.{" "}
            <button
              onClick={handleOpenPreferences}
              className="underline hover:text-dark-text transition-colors"
            >
              Cookie Settings
            </button>
          </p>

          {/* Buttons - Stacked */}
          <div className="flex flex-col gap-2">
            <button
              onClick={acceptAll}
              className="w-full py-2.5 bg-dark-text text-dark-surface-primary rounded-lg font-medium text-sm hover:opacity-90 transition-opacity"
            >
              Accept all
            </button>
            <button
              onClick={rejectAll}
              className={`w-full py-2.5 bg-transparent ${colors.text.primary} border ${colors.border.primary} rounded-lg font-medium text-sm hover:bg-dark-surface-primary transition-colors`}
            >
              Reject non-essential cookies
            </button>
          </div>
        </div>
      )}

      {/* Preferences Modal */}
      {showPreferencesModal && (
        <CookieSettingsModal
          analyticsEnabled={analyticsEnabled}
          marketingEnabled={marketingEnabled}
          onAnalyticsChange={setAnalyticsEnabled}
          onMarketingChange={setMarketingEnabled}
          onSave={handleSavePreferences}
          onAcceptAll={acceptAll}
          onClose={closePreferences}
        />
      )}
    </>
  );
}

interface CookieSettingsModalProps {
  analyticsEnabled: boolean;
  marketingEnabled: boolean;
  onAnalyticsChange: (enabled: boolean) => void;
  onMarketingChange: (enabled: boolean) => void;
  onSave: () => void;
  onAcceptAll: () => void;
  onClose: () => void;
}

function CookieSettingsModal({
  analyticsEnabled,
  marketingEnabled,
  onAnalyticsChange,
  onMarketingChange,
  onSave,
  onAcceptAll,
  onClose,
}: CookieSettingsModalProps) {
  const [expandedCategory, setExpandedCategory] = useState<string | null>(null);

  const toggleExpand = (categoryId: string) => {
    setExpandedCategory(expandedCategory === categoryId ? null : categoryId);
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-dark-surface-secondary w-full max-w-md rounded-2xl shadow-[0_8px_40px_0_rgba(0,0,0,0.6)] max-h-[90vh] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="p-6 pb-4">
          <div className="flex justify-end mb-4">
            <button
              onClick={onClose}
              className={`${colors.text.secondary} hover:text-dark-text transition-colors`}
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M18 6L6 18M6 6l12 12" />
              </svg>
            </button>
          </div>

          <h2 className={`${typography.size.xl} ${typography.weight.bold} ${colors.text.primary} mb-3`}>
            Cookie settings
          </h2>

          <p className={`${typography.size.sm} ${colors.text.primary} mb-2`}>
            Cookies help us to enhance your experience and improve our website.
          </p>

          <p className={`${typography.size.sm} ${colors.text.secondary}`}>
            Find out more on different cookie categories by expanding each cookie category below.
          </p>
        </div>

        {/* Cookie Categories */}
        <div className="px-6 pb-4 space-y-1 overflow-y-auto max-h-[40vh]">
          {/* Essential Cookies */}
          <CookieCategoryRow
            label="Essential cookies"
            description="Required for authentication and core functionality. These cookies are necessary for the website to function and cannot be disabled."
            isExpanded={expandedCategory === "essential"}
            onToggleExpand={() => toggleExpand("essential")}
            alwaysActive
          />

          {/* Analytics Cookies */}
          <CookieCategoryRow
            label="Analytics cookies"
            description="Help us understand how you use Darvin to improve the experience. This includes Google Analytics and Sentry error tracking."
            isExpanded={expandedCategory === "analytics"}
            onToggleExpand={() => toggleExpand("analytics")}
            enabled={analyticsEnabled}
            onToggle={() => onAnalyticsChange(!analyticsEnabled)}
          />

          {/* Advertising Cookies */}
          <CookieCategoryRow
            label="Advertising cookies"
            description="Used to measure the effectiveness of our advertising campaigns. This includes Meta Pixel and Reddit Pixel."
            isExpanded={expandedCategory === "advertising"}
            onToggleExpand={() => toggleExpand("advertising")}
            enabled={marketingEnabled}
            onToggle={() => onMarketingChange(!marketingEnabled)}
          />
        </div>

        {/* Footer Actions */}
        <div className="p-6 pt-4 flex gap-3">
          <button
            onClick={onSave}
            className="flex-1 py-3 bg-dark-text text-dark-surface-primary rounded-lg font-medium text-sm hover:opacity-90 transition-opacity"
          >
            Confirm my choices
          </button>
          <button
            onClick={onAcceptAll}
            className={`flex-1 py-3 bg-dark-surface-primary ${colors.text.primary} rounded-lg font-medium text-sm hover:bg-dark-hover transition-colors`}
          >
            Accept all
          </button>
        </div>
      </div>
    </div>
  );
}

interface CookieCategoryRowProps {
  label: string;
  description: string;
  isExpanded: boolean;
  onToggleExpand: () => void;
  alwaysActive?: boolean;
  enabled?: boolean;
  onToggle?: () => void;
}

function CookieCategoryRow({
  label,
  description,
  isExpanded,
  onToggleExpand,
  alwaysActive,
  enabled,
  onToggle,
}: CookieCategoryRowProps) {
  return (
    <div className="border-b border-dark-border last:border-b-0">
      <div className="flex items-center justify-between py-4">
        <button
          onClick={onToggleExpand}
          className="flex items-center gap-3 text-left flex-1"
        >
          <span className={`${colors.text.secondary} transition-transform ${isExpanded ? "rotate-45" : ""}`}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M8 3v10M3 8h10" />
            </svg>
          </span>
          <span className={`${typography.size.sm} ${typography.weight.semibold} ${colors.text.primary}`}>
            {label}
          </span>
        </button>

        {alwaysActive ? (
          <span className="text-emerald-400 text-sm font-medium">
            Always active
          </span>
        ) : (
          <button
            type="button"
            role="switch"
            aria-checked={enabled}
            onClick={onToggle}
            className={`
              relative inline-flex h-7 w-12 items-center rounded-full
              transition-colors duration-200 cursor-pointer
              ${enabled ? "bg-dark-text" : "bg-dark-surface-tertiary"}
            `}
          >
            <span
              className={`
                inline-block h-5 w-5 rounded-full
                transform transition-transform duration-200
                ${enabled ? "translate-x-6 bg-dark-surface-primary" : "translate-x-1 bg-dark-text-secondary"}
              `}
            />
          </button>
        )}
      </div>

      {/* Expandable description */}
      <div
        className={`overflow-hidden transition-all duration-200 ${
          isExpanded ? "max-h-40 pb-4" : "max-h-0"
        }`}
      >
        <p className={`${typography.size.sm} ${colors.text.secondary} pl-7`}>
          {description}
        </p>
      </div>
    </div>
  );
}
